// Popup „Alte Kundenakte zuweisen" im Reiter Dokumente (Plan 01.10.2026, B2):
// der umgekehrte Weg — man steht in der leeren Akte eines Kontakts und sucht
// den Ordner vom alten Share, der dazugehört. Ohne Fluent (kvp-rd `.dm`),
// Nachfolger des AkteZuweisenDialog: mit echter Trefferzahl (`X-Total-Count`)
// und Nachladen beim Scrollen statt einer stillen 40er-Grenze. Während einer
// neuen Suche bleibt die alte Liste gedimmt stehen, mit Hinweis.

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { apiUrl } from "../../utils/api";
import { I } from "../redesign/kvpIcons";
import { FolderIcon } from "../documents/explorer/fileTypeIcons";
import { Z_LAYERS } from "../windowManager/zLayers";
import { Folder, humanSize, kontaktKurz } from "./types";
import { bisherigerInhaber } from "./inhaber";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const SEITE = 40;
const datum = (s?: string | null) => (s ? new Date(s).toLocaleDateString("de-DE") : "—");

const zustand = (f: Folder): string => {
  if (f.notNeeded) return "nicht benötigt";
  if (f.matchStatus === "manual" || f.contact?.id) return `zugewiesen: ${kontaktKurz(f)}`;
  return "noch offen";
};

export const AkteZuweisenPopup: React.FC<{
  contactId: number;
  /** Vorschlag für die Suche — meist der Name des Kontakts. */
  suchvorschlag?: string;
  onClose: () => void;
  onAssigned: (folder: Folder) => void;
}> = ({ contactId, suchvorschlag, onClose, onAssigned }) => {
  const [q, setQ] = useState(suchvorschlag ?? "");
  const [treffer, setTreffer] = useState<Folder[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<Folder | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const generation = useRef(0);
  const listeRef = useRef<HTMLDivElement | null>(null);

  // Ohne Vorschlag mit dem Nachnamen des Kontakts suchen — so heißen die
  // Ordner auf dem alten Share fast immer.
  useEffect(() => {
    if (suchvorschlag !== undefined) return;
    const ctrl = new AbortController();
    fetch(apiUrl(`contact/${contactId}`), { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((c: { lastname?: string | null } | null) => {
        const name = c?.lastname?.trim();
        if (name) setQ((alt) => alt || name);
      })
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [contactId, suchvorschlag]);

  const lade = React.useCallback(
    async (offset: number, gen: number, signal?: AbortSignal) => {
      const res = await fetch(
        apiUrl("kundenakten-folders", { q: q.trim(), scope: "all", limit: SEITE, offset }),
        { signal },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const tot = res.headers.get("X-Total-Count") ?? res.headers.get("x-total-count");
      const data = await res.json();
      if (gen !== generation.current) return;
      const arr: Folder[] = (Array.isArray(data) ? data : []).filter((f: Folder) => !f.missing);
      setTreffer((alt) => (offset ? [...alt, ...arr] : arr));
      if (tot != null) setTotal(Number(tot) || 0);
    },
    [q],
  );

  useEffect(() => {
    const gen = ++generation.current;
    const ctrl = new AbortController();
    setLaedt(true);
    const t = setTimeout(async () => {
      try {
        await lade(0, gen, ctrl.signal);
        listeRef.current?.scrollTo({ top: 0 });
      } catch (e) {
        if ((e as { name?: string })?.name !== "AbortError" && gen === generation.current) {
          setTreffer([]);
          setTotal(null);
          setFehler("Die Ordner konnten nicht geladen werden.");
        }
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [lade]);

  const mehrDa = total != null && treffer.length < total;
  const nachladen = async () => {
    if (laedt || !mehrDa) return;
    const gen = generation.current;
    setLaedt(true);
    try {
      await lade(treffer.length, gen);
    } catch {
      /* die Liste bleibt, der nächste Scroll versucht es erneut */
    } finally {
      if (gen === generation.current) setLaedt(false);
    }
  };
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) void nachladen();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const zuweisen = async () => {
    if (!gewaehlt) return;
    setBusy(true);
    setFehler(null);
    try {
      const res = await fetch(apiUrl(`kundenakten-folders/${gewaehlt.id}/assign`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactIds: [contactId] }),
      });
      if (!res.ok) {
        throw new Error(
          res.status === 403
            ? "Keine Schreibrechte auf diesen Kontakt."
            : res.status === 404
              ? "Ordner oder Kontakt nicht gefunden."
              : `Zuweisung fehlgeschlagen (HTTP ${res.status}).`,
        );
      }
      onAssigned(await res.json());
      onClose();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const schonVergeben = gewaehlt ? bisherigerInhaber(gewaehlt, contactId) : null;

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" style={{ zIndex: Z_LAYERS.windowConfirm }} data-testid="akte-zuweisen-scrim">
      <div className="dm" role="dialog" aria-modal="true" aria-labelledby="akte-zuweisen-titel" style={{ width: "min(680px, 100%)" }}>
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Reiter Dokumente</div>
              <h2 id="akte-zuweisen-titel">Alte Kundenakte zuweisen</h2>
            </div>
            <button type="button" className="dm-x" onClick={onClose} aria-label="Schließen" disabled={busy}>
              ×
            </button>
          </div>
        </div>
        <div className="dm-body" style={{ display: "grid", gap: 10 }}>
          <div className="ak-pop-hinweis">
            Ordner vom alten Share suchen und diesem Kontakt zuweisen. Die Dokumente werden danach aus dem
            Speicher gebucht, in der Regel binnen zwei Minuten.
          </div>
          <div className="search" style={{ minWidth: 0 }}>
            {I.search}
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ordnername oder Nachname …"
              autoFocus
              aria-label="Ordner suchen"
            />
          </div>
          <div className="ak-pop-zaehler">
            {total == null ? (laedt ? "Suche …" : "") : `${total} Treffer`}
            {laedt && total != null ? " · wird aktualisiert …" : ""}
          </div>
          <div
            ref={listeRef}
            role="listbox"
            aria-label="Gefundene Ordner"
            aria-busy={laedt}
            className={"ak-pop-liste" + (laedt && treffer.length ? " laedt" : "")}
            onScroll={onScroll}
          >
            {treffer.length === 0 ? (
              <div className="ak-pop-leer">{laedt ? "Suche …" : "Kein Ordner gefunden."}</div>
            ) : (
              treffer.map((f) => {
                const an = gewaehlt?.id === f.id;
                return (
                  <div
                    key={f.id}
                    role="option"
                    aria-selected={an}
                    className={"ak-pop-zeile" + (an ? " on" : "")}
                    onClick={() => setGewaehlt(f)}
                  >
                    <FolderIcon size={18} />
                    <span className="ak-pop-name" title={f.folderName}>{f.folderName}</span>
                    <span className="ak-muted" title="Zuletzt geändert">{datum(f.lastModifiedAt)}</span>
                    <span className="ak-muted ak-pop-zustand">{zustand(f)}</span>
                    <span className="ak-muted">{f.fileCount ?? "?"} Dateien · {humanSize(f.sizeBytes)}</span>
                  </div>
                );
              })
            )}
            {mehrDa && (
              <button type="button" className="ak-link ak-pop-mehr" onClick={() => void nachladen()} disabled={laedt}>
                {laedt ? "Lade …" : `Weitere laden (${total! - treffer.length} offen)`}
              </button>
            )}
          </div>
          {schonVergeben && (
            <div className="ak-muted" style={{ whiteSpace: "normal" }}>
              Dieser Ordner ist bisher <b>{schonVergeben}</b> zugewiesen und wird auf diesen Kontakt umgehängt.
              Bereits gebuchte Dokumente bleiben dort erhalten.
            </div>
          )}
          {fehler && <div className="ak-fehler" role="alert">{fehler}</div>}
        </div>
        <div className="dm-foot">
          <button type="button" className="tbtn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="button" className="tbtn primary" onClick={() => void zuweisen()} disabled={!gewaehlt || busy}>
            {busy ? "Bitte warten …" : "Zuweisen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AkteZuweisenPopup;
